import { useEffect, useMemo, useRef, useState } from 'react';
import { motion as Motion, useMotionValueEvent, useScroll, useTransform } from 'framer-motion';
import { SDMonogram } from '../branding/BrandMark';

const EASE = [0.22, 1, 0.36, 1];
const COLUMN_WIDTH = 304;
const COLUMN_GAP = 40;
const TRACK_PADDING = 88;
const TRACK_HEIGHT = 620;
const TOP_CARD_Y = 62;
const CENTER_CARD_Y = 214;
const BOTTOM_CARD_Y = 368;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function cardLeft(column) {
  return TRACK_PADDING + column * (COLUMN_WIDTH + COLUMN_GAP);
}

function cardCenter(column) {
  return cardLeft(column) + COLUMN_WIDTH / 2;
}

function buildTimelineModel(content) {
  const [founderA, founderB] = content.paths;
  const preMergeColumns = Math.max(founderA.milestones.length, founderB.milestones.length);
  const mergeColumn = preMergeColumns;
  const sharedColumns = content.shared.length;
  const totalColumns = preMergeColumns + 1 + sharedColumns;
  const trackWidth =
    TRACK_PADDING * 2 + totalColumns * COLUMN_WIDTH + Math.max(0, totalColumns - 1) * COLUMN_GAP;

  const topCards = founderA.milestones.map((milestone, index) => ({
    ...milestone,
    column: index,
    lane: 'top',
    founder: founderA,
  }));

  const bottomCards = founderB.milestones.map((milestone, index) => ({
    ...milestone,
    column: index,
    lane: 'bottom',
    founder: founderB,
  }));

  const mergeCard = {
    ...content.merge,
    column: mergeColumn,
    lane: 'center',
    founder: {
      name: content.brandName,
      role: 'Company Formation',
      tone: 'merged',
    },
    kind: 'merge',
  };

  const sharedCards = content.shared.map((milestone, index) => ({
    ...milestone,
    column: mergeColumn + 1 + index,
    lane: 'center',
    founder: {
      name: content.brandName,
      role: milestone.phase,
      tone: 'merged',
    },
    kind: 'shared',
  }));

  return {
    founderA,
    founderB,
    topCards,
    bottomCards,
    mergeCard,
    sharedCards,
    preMergeColumns,
    mergeColumn,
    totalColumns,
    trackWidth,
    mergeX: cardCenter(mergeColumn),
  };
}

function MilestoneCard({ milestone, left, top, activeColumn, reducedMotion = false }) {
  const distance = Math.abs(milestone.column - activeColumn);
  const isActive = distance === 0;
  const scale   = isActive ? 1.04 : distance === 1 ? 0.99 : 0.97;
  const opacity = isActive ? 1    : distance === 1 ? 0.92 : 0.80;

  return (
    <Motion.div
      className="company-history-card-shell"
      style={{ left, top }}
      initial={reducedMotion ? undefined : { opacity: 0, y: 28 }}
      whileInView={
        reducedMotion
          ? undefined
          : {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                delay: milestone.column * 0.04,
                ease: EASE,
              },
            }
      }
      viewport={{ once: true, amount: 0.45 }}
    >
      <Motion.article
        className={`company-history-card company-history-card--${milestone.founder.tone} ${milestone.kind === 'merge' ? 'is-merge' : ''}`}
        animate={reducedMotion ? undefined : { scale, opacity }}
        transition={{ duration: reducedMotion ? 0.2 : 0.55, ease: EASE }}
        whileHover={
          reducedMotion
            ? undefined
            : {
                y: -6,
                rotateX: 1.8,
                rotateY:
                  milestone.founder.tone === 'systems'
                    ? -2.6
                    : milestone.founder.tone === 'experience'
                      ? 2.6
                      : 0,
                scale: isActive ? 1.06 : 1,
                transition: { duration: 0.3, ease: EASE },
              }
        }
      >
        <div className="company-history-card__glow" aria-hidden="true" />
        <div className="company-history-card__meta">
          <div>
            <span className="company-history-card__eyebrow">{milestone.founder.name}</span>
            <strong>{milestone.founder.role}</strong>
          </div>
          <span className="company-history-card__year">{milestone.year}</span>
        </div>
        <h3>{milestone.title}</h3>
        <p>{milestone.summary}</p>
        <div className="company-history-card__tags">
          {milestone.tags.map((tag) => (
            <span key={`${milestone.title}-${tag}`}>{tag}</span>
          ))}
        </div>
      </Motion.article>
    </Motion.div>
  );
}

function MobileMilestoneCard({ item, reducedMotion = false }) {
  return (
    <Motion.article
      className={`company-history-mobile-card company-history-mobile-card--${item.founder.tone} ${item.kind === 'merge' ? 'is-merge' : ''}`}
      initial={reducedMotion ? undefined : { opacity: 0, y: 22 }}
      whileInView={
        reducedMotion
          ? undefined
          : {
              opacity: 1,
              y: 0,
              transition: { duration: 0.72, ease: EASE },
            }
      }
      viewport={{ once: true, amount: 0.18 }}
      whileHover={
        reducedMotion
          ? undefined
          : {
              y: -4,
              transition: { duration: 0.24, ease: EASE },
            }
      }
    >
      {item.kind === 'merge' ? (
        <div className="company-history-mobile-card__merge-mark">
          <SDMonogram className="company-history-mobile-card__merge-logo" compact rings={false} />
        </div>
      ) : null}
      <div className="company-history-mobile-card__meta">
        <div>
          <span>{item.founder.name}</span>
          <strong>{item.founder.role}</strong>
        </div>
        <span>{item.year}</span>
      </div>
      <h3>{item.title}</h3>
      <p>{item.summary}</p>
      <div className="company-history-mobile-card__tags">
        {item.tags.map((tag) => (
          <span key={`${item.title}-${tag}`}>{tag}</span>
        ))}
      </div>
    </Motion.article>
  );
}

export default function CompanyHistoryTimeline({ content, reducedMotion = false }) {
  const sectionRef = useRef(null);
  const viewportRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [travel, setTravel] = useState(0);
  const [activeColumn, setActiveColumn] = useState(0);

  const model = useMemo(() => buildTimelineModel(content), [content]);
  const sectionHeight = isMobile ? undefined : `calc(100vh + ${travel + 140}px)`;

  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)');
    const sync = () => {
      setIsMobile(media.matches);
    };

    sync();
    media.addEventListener('change', sync);

    return () => {
      media.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      return undefined;
    }

    const sync = () => {
      const viewportWidth = viewportRef.current?.clientWidth ?? 0;
      const nextTravel = Math.max(0, model.trackWidth - viewportWidth);

      setTravel((current) => (Math.abs(current - nextTravel) < 1 ? current : nextTravel));
    };

    sync();

    const observer = new ResizeObserver(sync);

    if (viewportRef.current) {
      observer.observe(viewportRef.current);
    }

    window.addEventListener('resize', sync);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [isMobile, model.trackWidth]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: isMobile ? ['start 80%', 'end 20%'] : ['start start', 'end end'],
  });

  const x = useTransform(scrollYProgress, [0, 1], [0, -travel]);
  const mergeProgress =
    model.totalColumns > 1 ? model.mergeColumn / Math.max(1, model.totalColumns - 1) : 1;
  const preMergeGlow = useTransform(scrollYProgress, [0, mergeProgress], [0.04, 1]);
  const mergedGlow = useTransform(scrollYProgress, [mergeProgress, 1], [0.04, 1]);
  const mergeLogoScale = useTransform(
    scrollYProgress,
    [Math.max(0, mergeProgress - 0.12), mergeProgress, Math.min(1, mergeProgress + 0.18)],
    [0.84, 1.05, 1],
  );
  const mergeLogoOpacity = useTransform(
    scrollYProgress,
    [Math.max(0, mergeProgress - 0.12), mergeProgress, Math.min(1, mergeProgress + 0.18)],
    [0.18, 0.62, 0.32],
  );

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    if (isMobile) {
      return;
    }

    const nextColumn = clamp(
      Math.round(latest * Math.max(1, model.totalColumns - 1)),
      0,
      Math.max(0, model.totalColumns - 1),
    );

    setActiveColumn((current) => (current === nextColumn ? current : nextColumn));
  });

  const topPathY = TOP_CARD_Y + 122;
  const centerPathY = CENTER_CARD_Y + 122;
  const bottomPathY = BOTTOM_CARD_Y + 52;
  const pathStartX = cardCenter(0) - 124;
  const pathEndX = cardCenter(model.totalColumns - 1) + 128;

  const topPath = `M ${pathStartX} ${topPathY} C ${cardCenter(1) - 68} ${topPathY}, ${model.mergeX - 184} ${topPathY}, ${model.mergeX} ${centerPathY}`;
  const bottomPath = `M ${pathStartX} ${bottomPathY} C ${cardCenter(1) - 68} ${bottomPathY}, ${model.mergeX - 184} ${bottomPathY}, ${model.mergeX} ${centerPathY}`;
  const mergedPath = `M ${model.mergeX} ${centerPathY} C ${model.mergeX + 150} ${centerPathY}, ${pathEndX - 180} ${centerPathY}, ${pathEndX} ${centerPathY}`;

  const mobileTimeline = useMemo(() => {
    const sequence = [];
    const loops = Math.max(model.founderA.milestones.length, model.founderB.milestones.length);

    for (let index = 0; index < loops; index += 1) {
      if (model.founderA.milestones[index]) {
        sequence.push({
          ...model.founderA.milestones[index],
          founder: model.founderA,
          kind: 'path',
        });
      }

      if (model.founderB.milestones[index]) {
        sequence.push({
          ...model.founderB.milestones[index],
          founder: model.founderB,
          kind: 'path',
        });
      }
    }

    sequence.push(model.mergeCard);

    model.sharedCards.forEach((item) => {
      sequence.push(item);
    });

    return sequence;
  }, [model]);

  return (
    <section
      ref={sectionRef}
      className="story-section story-section--founders company-history"
      style={sectionHeight ? { minHeight: sectionHeight } : undefined}
    >
      <div className="company-history__ambient company-history__ambient--left" aria-hidden="true" />
      <div className="company-history__ambient company-history__ambient--right" aria-hidden="true" />

      <div className="company-history__shell">
        <div className="section-header company-history__header">
          <Motion.span
            className="eyebrow"
            initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, transition: { duration: 0.72, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.45 }}
          >
            {content.eyebrow}
          </Motion.span>
          <Motion.h2
            className="section-title"
            initial={reducedMotion ? undefined : { opacity: 0, y: 24 }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, transition: { duration: 0.78, delay: 0.05, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.45 }}
          >
            {content.title}
          </Motion.h2>
          <Motion.p
            className="section-description"
            initial={reducedMotion ? undefined : { opacity: 0, y: 24 }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, transition: { duration: 0.82, delay: 0.1, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.45 }}
          >
            {content.description}
          </Motion.p>
        </div>

        {isMobile ? (
          <div className="company-history-mobile">
            <div className="company-history-mobile__rail" aria-hidden="true" />
            {mobileTimeline.map((item) => (
              <div key={`${item.founder.name}-${item.title}`} className="company-history-mobile__item">
                <div className={`company-history-mobile__dot company-history-mobile__dot--${item.founder.tone}`} />
                <MobileMilestoneCard item={item} reducedMotion={reducedMotion} />
              </div>
            ))}
          </div>
        ) : (
          <div ref={viewportRef} className="company-history__sticky">
            <div className="company-history__legend">
              {[model.founderA, model.founderB].map((founder) => (
                <div key={founder.name} className={`company-history__legend-pill company-history__legend-pill--${founder.tone}`}>
                  <span className="company-history__legend-dot" />
                  <div>
                    <strong>{founder.name}</strong>
                    <span>{founder.role}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="company-history__viewport">
              <Motion.div className="company-history__track" style={{ width: model.trackWidth, x }}>
                <svg
                  className="company-history__paths"
                  viewBox={`0 0 ${model.trackWidth} ${TRACK_HEIGHT}`}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path d={topPath} className="company-history__path company-history__path--base" />
                  <path d={bottomPath} className="company-history__path company-history__path--base" />
                  <path d={mergedPath} className="company-history__path company-history__path--base" />

                  <Motion.path
                    d={topPath}
                    className="company-history__path company-history__path--glow"
                    style={reducedMotion ? undefined : { pathLength: preMergeGlow }}
                  />
                  <Motion.path
                    d={bottomPath}
                    className="company-history__path company-history__path--glow"
                    style={reducedMotion ? undefined : { pathLength: preMergeGlow }}
                  />
                  <Motion.path
                    d={mergedPath}
                    className="company-history__path company-history__path--glow"
                    style={reducedMotion ? undefined : { pathLength: mergedGlow }}
                  />
                </svg>

                <Motion.div
                  className="company-history__merge-mark"
                  style={
                    reducedMotion
                      ? { left: model.mergeX - 52, top: CENTER_CARD_Y - 84 }
                      : {
                          left: model.mergeX - 52,
                          top: CENTER_CARD_Y - 84,
                          scale: mergeLogoScale,
                          opacity: mergeLogoOpacity,
                        }
                  }
                >
                  <div className="company-history__merge-halo" />
                  <SDMonogram className="company-history__merge-logo" compact rings={false} />
                </Motion.div>

                {model.topCards.map((milestone) => (
                  <MilestoneCard
                    key={`${milestone.founder.name}-${milestone.title}`}
                    milestone={milestone}
                    left={cardLeft(milestone.column)}
                    top={TOP_CARD_Y}
                    activeColumn={activeColumn}
                    reducedMotion={reducedMotion}
                  />
                ))}

                {model.bottomCards.map((milestone) => (
                  <MilestoneCard
                    key={`${milestone.founder.name}-${milestone.title}`}
                    milestone={milestone}
                    left={cardLeft(milestone.column)}
                    top={BOTTOM_CARD_Y}
                    activeColumn={activeColumn}
                    reducedMotion={reducedMotion}
                  />
                ))}

                {[model.mergeCard, ...model.sharedCards].map((milestone) => (
                  <MilestoneCard
                    key={`${milestone.founder.name}-${milestone.title}`}
                    milestone={milestone}
                    left={cardLeft(milestone.column)}
                    top={CENTER_CARD_Y}
                    activeColumn={activeColumn}
                    reducedMotion={reducedMotion}
                  />
                ))}
              </Motion.div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
